import React from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { 
  Crown, HeartHandshake, UsersRound, TreePine, 
  Zap, ShieldCheck, Eye, RotateCcw,
  ArrowRight 
} from "lucide-react";
import { mockData } from "../mock";

const iconMap = {
  Shield: Crown, // Conscious Leadership
  Heart: HeartHandshake, // Human-Centric Culture & Team Cohesion
  Users: UsersRound, // Products & Services
  Lightbulb: Zap, // Customers & Clients
  Leaf: TreePine, // Community & Conscious Social Impact
  Eye: ShieldCheck, // Brand Vision & Collaboration
  Lock: ShieldCheck,
  RefreshCw: RotateCcw
};

const PillarsSection = () => {
  const { pillars } = mockData;

  return (
    <section className="py-32 relative overflow-hidden">
      {/* WOW FACTOR Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-blue-50/30 via-transparent to-purple-50/30"></div>
      
      {/* Dynamic Elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-emerald-200/20 to-teal-300/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-cyan-200/15 to-blue-300/15 rounded-full blur-3xl animate-bounce"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Header - Welcome.com Style */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 mb-4 bg-emerald-50 text-emerald-700 rounded-full font-medium text-sm">
            6 Pillars Framework
          </div>
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-gray-900 mb-6 leading-tight">
            Compassionate Excellence
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-600"> 
              FRAMEWORK
            </span>
          </h2>
          
          <p className="text-base md:text-lg text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            {pillars.description}
          </p>
        </div>

        {/* Six Pillars Circular Chart */}
        <div className="max-w-4xl mx-auto mb-20">
          <div className="relative group">
            {/* Floating elements around image */}
            <div className="absolute -top-8 -left-8 w-16 h-16 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full blur-lg opacity-70 animate-pulse"></div>
            <div className="absolute -bottom-8 -right-8 w-20 h-20 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full blur-xl opacity-60 animate-bounce"></div>
            
            <div className="relative rounded-3xl overflow-hidden shadow-2xl transform group-hover:scale-105 transition-all duration-700 bg-white p-8">
              <img 
                src="/six_pillars_chart.png"
                alt="CNESS 6 Pillars Framework - Compassionate Excellence"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>

        {/* Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto mb-12">
          {pillars.pillars.map((pillar, index) => {
            const IconComponent = iconMap[pillar.icon];
            
            // Define unique color themes for each pillar
            const colorThemes = [
              { from: 'from-red-100', to: 'to-red-200', hoverFrom: 'group-hover:from-red-500', hoverTo: 'group-hover:to-red-600', iconColor: 'text-red-600', hoverText: 'group-hover:text-red-600' },
              { from: 'from-blue-100', to: 'to-blue-200', hoverFrom: 'group-hover:from-blue-500', hoverTo: 'group-hover:to-blue-600', iconColor: 'text-blue-600', hoverText: 'group-hover:text-blue-600' },
              { from: 'from-green-100', to: 'to-green-200', hoverFrom: 'group-hover:from-green-500', hoverTo: 'group-hover:to-green-600', iconColor: 'text-green-600', hoverText: 'group-hover:text-green-600' },
              { from: 'from-purple-100', to: 'to-purple-200', hoverFrom: 'group-hover:from-purple-500', hoverTo: 'group-hover:to-purple-600', iconColor: 'text-purple-600', hoverText: 'group-hover:text-purple-600' },
              { from: 'from-amber-100', to: 'to-amber-200', hoverFrom: 'group-hover:from-amber-500', hoverTo: 'group-hover:to-amber-600', iconColor: 'text-amber-600', hoverText: 'group-hover:text-amber-600' },
              { from: 'from-teal-100', to: 'to-teal-200', hoverFrom: 'group-hover:from-teal-500', hoverTo: 'group-hover:to-teal-600', iconColor: 'text-teal-600', hoverText: 'group-hover:text-teal-600' },
              { from: 'from-indigo-100', to: 'to-indigo-200', hoverFrom: 'group-hover:from-indigo-500', hoverTo: 'group-hover:to-indigo-600', iconColor: 'text-indigo-600', hoverText: 'group-hover:text-indigo-600' },
              { from: 'from-pink-100', to: 'to-pink-200', hoverFrom: 'group-hover:from-pink-500', hoverTo: 'group-hover:to-pink-600', iconColor: 'text-pink-600', hoverText: 'group-hover:text-pink-600' }
            ];
            
            const theme = colorThemes[index % colorThemes.length];
            
            return (
              <Card 
                key={pillar.id}
                className="group hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer border border-gray-100 bg-white overflow-hidden"
              >
                <div className="p-6 text-center">
                  <div className={`w-12 h-12 mx-auto mb-4 rounded-lg bg-gradient-to-br ${theme.from} ${theme.to} flex items-center justify-center`}>
                    <IconComponent className={`h-6 w-6 ${theme.iconColor}`} />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">
                    {pillar.title}
                  </h3>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    {pillar.description}
                  </p>
                </div>
              </Card>
            );
          })}
        </div>

        <div className="text-center">
          <a href="/pillars">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-3 text-base font-semibold hover:from-emerald-700 hover:to-teal-700 shadow-lg hover:shadow-xl transition-all duration-300 rounded-full"
            >
              Explore Framework
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default PillarsSection;